// @code-guard: skip-file reason="Large Scenario Lens table orchestrator; split into child components/hooks in follow-up"
import { LockOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Spin, Table, Tooltip } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Dispatch, Key, SetStateAction, ThHTMLAttributes } from 'react';
import styled from '@emotion/styled';

import DownSvg from 'assets/svg/Down';
import { colorConstants } from 'config/colorConstants';
import { useFillHeightToFitScreen } from 'hooks/general/useFillHeightToFitScreen';
import {
    GenericCheckboxControl,
    GenericCheckboxTooltipList,
    GenericDatePickerControl,
    GenericDropdownControl,
    GenericPrefixedInputBoxControl,
    GenericSearchableDropdownControl,
} from 'components/molecules/PPA/commons';
import AiColumnHeaderTooltip from './aiColumnHeaderTooltip/AiColumnHeaderTooltip';
import DimensionColumnHeader, { type DimensionSortOrder } from './DimensionColumnHeader';
import ColumnFilterSortHeader from './ColumnFilterSortHeader';
import type { ColumnFilterSortValue } from './ColumnFilterSortPanel';
import KpiColumnGroupHeader from './KpiColumnGroupHeader';
import { DEFAULT_KPI_SORT_OPTIONS, type KpiSortingValue } from './KpiSortingPanel';
import TwoLineCellText from './TwoLineCellText';
import { useGenericAntdScenarioTable, type GenericScenarioTableUiConfig } from './useGenericAntdScenarioTable';
import type { GenericScenarioTableColumnConfig } from './genericScenarioTable.types';
import type { SimulatorAppliedFilters } from 'components/molecules/PricingSimulatorV3/SimulatorFilterPanel/SimulatorFilterPanel';
import type {
    SimulatorTableSearchInput,
    SimulatorTableSortPayload,
} from 'components/molecules/PricingSimulatorV3/SimulatorFilterPanel/simulatorFilter.helpers';
import type { PPAScenarioColumnVisibilityInput } from 'types/ppaViews';
import type { SimulationTableGroupByItem } from './simulationTable.helpers';
import {
    PPA_SIM_TABLE,
    PPA_SIM_TABLE_ADD_ROW_BTN,
    PPA_SIM_TABLE_AI_HEADER,
    PPA_SIM_TABLE_AI_INFO_BTN,
    PPA_SIM_TABLE_EMPTY,
    PPA_SIM_TABLE_FOOTER,
    PPA_SIM_TABLE_LOADING,
    PPA_SIM_TABLE_SELECT_ALL,
    ppaSimTableColumnHeaderTestId,
    ppaSimTableColumnSelectAllTestId,
    ppaSimTableRowSelectTestId,
} from 'constants/ppaTestIds';

type GenericAntdScenarioTableProps = {
    tableUiConfig: GenericScenarioTableUiConfig;
    sourceRows: any[];
    isLoading?: boolean;
    tableSort: SimulatorTableSortPayload | null;
    setTableSort: (sort: SimulatorTableSortPayload | null) => void;
    columnFilterSortByKey: Record<string, ColumnFilterSortValue>;
    setColumnFilterSortByKey: Dispatch<SetStateAction<Record<string, ColumnFilterSortValue>>>;
};

const getKpiDataIndex = (groupKey: string, metricKey: string) => `${groupKey}_${metricKey}`;

const getCellId = (rowKey: Key, columnKey: string) => `${String(rowKey)}:${columnKey}`;

const toApiSortOrder = (order: DimensionSortOrder): SimulatorTableSortPayload['order'] =>
    order === 'descend' ? 'desc' : 'asc';

const toUiSortOrder = (order: SimulatorTableSortPayload['order']): DimensionSortOrder =>
    order === 'desc' ? 'descend' : 'ascend';

const DEFAULT_KPI_SORT_VALUE: KpiSortingValue = {
    direction: 'desc',
    optionKey: DEFAULT_KPI_SORT_OPTIONS[0].key,
};

const resolveDisplayText = (value: any, displayFormat: any, emptyDisplay: string) => {
    const rawValue = String(value ?? '').trim();
    if (!rawValue) {
        if (displayFormat?.emptyDisplay !== undefined) return String(displayFormat.emptyDisplay);
        return emptyDisplay;
    }
    return `${displayFormat?.prefix ?? ''}${rawValue}${displayFormat?.suffix ?? ''}`;
};

const isAllDimensionValue = (value: any, allDimensionValue: string) =>
    String(value ?? '').trim().toLowerCase() === allDimensionValue.toLowerCase();

const isAiExpandableLabel = (value: any, expandableLabels: string[]) => {
    const normalizedValue = String(value ?? '').trim().toLowerCase();
    if (!normalizedValue) return false;
    if (!expandableLabels.length) return true;
    return expandableLabels.some((label) => label.toLowerCase() === normalizedValue);
};

const rowHasExpandableChildren = (record: any) => Array.isArray(record?.children) && record.children.length > 0;

const getDimensionColumnKeys = (columns: GenericScenarioTableColumnConfig[]) =>
    columns.filter((columnConfig) => columnConfig.isDimension).map((columnConfig) => columnConfig.key);

const getFlattenedHiddenDimensionKeys = (_columns: GenericScenarioTableColumnConfig[]) => new Set<string>();

const getExpandableDimensionKey = (
    record: any,
    dimensionColumnKeys: string[],
    allDimensionValue: string,
): string | null => {
    if (!rowHasExpandableChildren(record)) return null;

    return (
        dimensionColumnKeys.find((columnKey) => {
            if (!isAllDimensionValue(record[columnKey], allDimensionValue)) return false;
            return record.children.some(
                (child: any) => String(child[columnKey] ?? '').trim() !== String(record[columnKey] ?? '').trim(),
            );
        }) ?? null
    );
};

const collectExpandableRowKeys = (rows: any[]): Key[] => {
    const expandableRowKeys: Key[] = [];

    const visit = (row: any) => {
        const children = Array.isArray(row.children) ? row.children : [];
        if (!children.length) return;
        expandableRowKeys.push(row.key);
        children.forEach(visit);
    };

    rows.forEach(visit);
    return expandableRowKeys;
};

const flattenTableRows = (rows: any[]): any[] => {
    const flattenedRows: any[] = [];

    const visit = (row: any) => {
        const children = Array.isArray(row.children) ? row.children : [];

        if (!children.length) {
            const { children: _unusedChildren, ...rowWithoutChildren } = row;
            flattenedRows.push({
                ...rowWithoutChildren,
                ai: row.ai || '',
                isParent: false,
                parentHint: undefined,
            });
            return;
        }

        children.forEach(visit);
    };

    rows.forEach(visit);
    return flattenedRows;
};

const collectAllRows = (rows: any[]): any[] => {
    const allRows: any[] = [];

    const visit = (row: any) => {
        allRows.push(row);
        const children = Array.isArray(row.children) ? row.children : [];
        children.forEach(visit);
    };

    rows.forEach(visit);
    return allRows;
};

const isCheckboxChecked = (value: any) => value === true || value === 'true';

const collectVisibleTableRows = (rows: any[], expandedRowKeys: Key[], isFlattened: boolean): any[] => {
    if (isFlattened) return rows;

    const visibleRows: any[] = [];

    const visit = (row: any) => {
        visibleRows.push(row);
        const children = Array.isArray(row.children) ? row.children : [];
        if (!children.length || !expandedRowKeys.includes(row.key)) return;
        children.forEach(visit);
    };

    rows.forEach(visit);
    return visibleRows;
};

const getRepeatedDimensionCellIds = (
    visibleRows: any[],
    dimensionColumns: GenericScenarioTableColumnConfig[],
    getResolvedValue: (row: any, columnKey: string, dataIndex: string) => string,
    allDimensionValue: string,
): Set<string> => {
    const repeatedCellIds = new Set<string>();

    for (let rowIndex = 1; rowIndex < visibleRows.length; rowIndex += 1) {
        const previousRow = visibleRows[rowIndex - 1];
        const currentRow = visibleRows[rowIndex];

        dimensionColumns.forEach((columnConfig) => {
            const currentValue = getResolvedValue(currentRow, columnConfig.key, columnConfig.dataIndex);
            if (!currentValue || isAllDimensionValue(currentValue, allDimensionValue)) return;

            const previousValue = getResolvedValue(previousRow, columnConfig.key, columnConfig.dataIndex);
            if (!previousValue || isAllDimensionValue(previousValue, allDimensionValue)) return;
            if (currentValue !== previousValue) return;

            repeatedCellIds.add(getCellId(currentRow.key, columnConfig.key));
        });
    }

    return repeatedCellIds;
};

const StyledTableShell = styled.div`
    position: relative;
    min-height: 120px;
    background: ${colorConstants.white};
    border-radius: 4px;
    border: 1px solid ${colorConstants.tooltipDividerGrey};
    overflow: hidden;

    .ant-table-thead > tr > th {
        background: ${colorConstants.tableHoverCrossFill};
        color: ${colorConstants.blackTitle};
        border-color: ${colorConstants.tableCellBorder} !important;
        padding-top: 8px;
        padding-bottom: 8px;
        overflow: visible !important;
    }

    .ant-table-thead > tr:first-of-type > th {
        font-size: 12px;
        font-weight: 600;
    }

    .ant-table-thead > tr:nth-of-type(2) > th {
        font-size: 12px;
        font-weight: 400;
        padding-top: 4px;
        padding-bottom: 4px;
    }

    .ant-table-tbody > tr > td {
        background: ${colorConstants.white};
        font-size: 13px;
        color: ${colorConstants.tableBodyText};
        padding-top: 10px;
        padding-bottom: 10px;
        border-bottom: 0 !important;
        box-shadow: inset 0 -1px 0 ${colorConstants.tableCellBorder};
        white-space: normal;
        word-break: break-word;
    }

    .ant-table-cell::before {
        display: none !important;
    }

    .ant-table-thead .ant-table-cell-fix-left,
    .ant-table-thead .ant-table-selection-column {
        background: ${colorConstants.tableHoverCrossFill} !important;
    }

    .ant-table-tbody .ant-table-cell-fix-left,
    .ant-table-tbody .ant-table-selection-column {
        background: ${colorConstants.white} !important;
        box-shadow: inset 0 -1px 0 ${colorConstants.tableCellBorder};
    }

    .ant-table-tbody > tr.ant-table-row:hover > td,
    .ant-table-tbody > tr.ant-table-row:hover > .ant-table-cell-fix-left,
    .ant-table-tbody > tr.ant-table-row:hover > .ant-table-selection-column,
    .ant-table-tbody > tr.ant-table-row-selected > td,
    .ant-table-tbody > tr.ant-table-row-selected > .ant-table-cell-fix-left,
    .ant-table-tbody > tr.ant-table-row-selected > .ant-table-selection-column,
    .ant-table-tbody > tr.ant-table-row-selected:hover > td,
    .ant-table-tbody > tr.ant-table-row-selected:hover > .ant-table-cell-fix-left,
    .ant-table-tbody > tr.ant-table-row-selected:hover > .ant-table-selection-column {
        background: ${colorConstants.white} !important;
        box-shadow: inset 0 -1px 0 ${colorConstants.tableCellBorder};
    }

    .ant-table-thead > tr > th:not(:last-child),
    .ant-table-tbody > tr > td:not(:last-child) {
        border-right: 2px solid ${colorConstants.tabBackground} !important;
    }

    .ant-table-thead > tr:first-of-type > th:last-child,
    .ant-table-tbody > tr > td:last-child {
        border-right: 0 !important;
    }

    .ant-table-thead > tr:nth-of-type(2) > th:last-child {
        border-right: 2px solid ${colorConstants.tabBackground} !important;
    }

    .ant-table-header {
        background: ${colorConstants.tableHoverCrossFill} !important;
    }

    .table-checkbox-control.ant-checkbox-wrapper,
    .ant-table-selection-column .ant-checkbox-wrapper {
        margin: 0;
        line-height: 1;
    }

    .table-checkbox-control .ant-checkbox,
    .ant-table-selection-column .ant-checkbox {
        top: 0;
    }

    .table-checkbox-control .ant-checkbox::after,
    .ant-table-selection-column .ant-checkbox::after {
        display: none !important;
    }

    .table-checkbox-control .ant-checkbox-inner,
    .ant-table-selection-column .ant-checkbox-inner {
        width: 16px;
        height: 16px;
        border-radius: 2px;
        border-color: ${colorConstants.tableCellBorder} !important;
        background: ${colorConstants.white};
    }

    .table-checkbox-control .ant-checkbox-checked .ant-checkbox-inner,
    .table-checkbox-control .ant-checkbox-indeterminate .ant-checkbox-inner,
    .ant-table-selection-column .ant-checkbox-checked .ant-checkbox-inner,
    .ant-table-selection-column .ant-checkbox-indeterminate .ant-checkbox-inner {
        background: ${colorConstants.violet} !important;
        border-color: ${colorConstants.violet} !important;
    }

    .table-checkbox-control .ant-checkbox-indeterminate .ant-checkbox-inner::after,
    .ant-table-selection-column .ant-checkbox-indeterminate .ant-checkbox-inner::after {
        top: 50%;
        left: 50%;
        display: block !important;
        width: 8px !important;
        height: 2px !important;
        border: 0 !important;
        border-radius: 1px;
        background: ${colorConstants.white} !important;
        transform: translate(-50%, -50%) scale(1) !important;
        opacity: 1 !important;
    }

    .table-checkbox-control .ant-checkbox-wrapper:hover .ant-checkbox-inner,
    .table-checkbox-control .ant-checkbox:hover .ant-checkbox-inner,
    .table-checkbox-control .ant-checkbox-input:focus + .ant-checkbox-inner,
    .ant-table-selection-column .ant-checkbox-wrapper:hover .ant-checkbox-inner,
    .ant-table-selection-column .ant-checkbox:hover .ant-checkbox-inner,
    .ant-table-selection-column .ant-checkbox-input:focus + .ant-checkbox-inner {
        border-color: ${colorConstants.tableCellBorder} !important;
    }

    .table-checkbox-control .ant-checkbox-wrapper:hover .ant-checkbox-checked .ant-checkbox-inner,
    .table-checkbox-control .ant-checkbox-checked:hover .ant-checkbox-inner,
    .table-checkbox-control .ant-checkbox-checked .ant-checkbox-input:focus + .ant-checkbox-inner,
    .table-checkbox-control .ant-checkbox-wrapper:hover .ant-checkbox-indeterminate .ant-checkbox-inner,
    .table-checkbox-control .ant-checkbox-indeterminate:hover .ant-checkbox-inner,
    .ant-table-selection-column .ant-checkbox-wrapper:hover .ant-checkbox-checked .ant-checkbox-inner,
    .ant-table-selection-column .ant-checkbox-checked:hover .ant-checkbox-inner,
    .ant-table-selection-column .ant-checkbox-checked .ant-checkbox-input:focus + .ant-checkbox-inner,
    .ant-table-selection-column .ant-checkbox-wrapper:hover .ant-checkbox-indeterminate .ant-checkbox-inner,
    .ant-table-selection-column .ant-checkbox-indeterminate:hover .ant-checkbox-inner {
        border-color: ${colorConstants.violet} !important;
    }

    .ant-table-body {
        overscroll-behavior: none;
    }

    .ant-table-selection-column {
        width: 44px !important;
        min-width: 44px !important;
    }

    .ant-table-thead > tr > th:hover, .ant-table-thead .ant-table-cell-fix-left:hover,
    .ant-table-thead .ant-table-selection-column:hover {
        background: ${colorConstants.tableHeaderHoverFill} !important;
    }

    .ant-table-thead > tr > th > * {
        width: 100%;
        min-width: 0;
    }

    .ant-table-thead .ant-table-cell-fix-left {
        overflow: visible !important;
    }

    .dimension-sort-header,
    .column-filter-sort-trigger,
    .kpi-sort-trigger {
        border: 0;
        background: transparent;
        padding: 0;
        cursor: pointer;
        color: inherit;
        font: inherit;
        opacity: 0;
        pointer-events: none;
    }

    .ant-table-thead .ant-table-cell:hover .dimension-sort-header,
    .ant-table-thead .ant-table-cell:hover .column-filter-sort-trigger,
    .ant-table-thead .ant-table-cell:hover .kpi-sort-trigger,
    .dimension-sort-header.is-sorted,
    .column-filter-sort-trigger.is-open,
    .column-filter-sort-trigger.is-sorted,
    .kpi-sort-trigger.is-open,
    .kpi-sort-trigger.is-applied {
        opacity: 1;
        pointer-events: auto;
    }

    .ant-table-thead th:hover .kpi-sort-trigger,
    .kpi-sort-trigger.is-open,
    .kpi-sort-trigger:hover {
        background: ${colorConstants.tableSortIconBg} !important;
    }

    .kpi-sort-settings-btn {
        border: 0;
        padding: 0;
        cursor: pointer;
    }

    .ai-expand-btn {
        border: 0;
        outline: 0;
        padding: 0;
        margin: 0;
        background: transparent;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
    }

    .dimension-expand-chevron {
        display: block;
        transition: transform 160ms ease-out;
    }

    .dimension-expand-chevron.is-expanded {
        transform: rotate(180deg);
    }

    .table-dropdown {
        width: 100%;
        opacity: 0.55;
        transition: opacity 160ms ease-out;
    }

    .table-dropdown.is-open,
    .table-dropdown:focus-within,
    .table-dropdown.is-row-selected {
        opacity: 1;
    }

    .table-dropdown > button,
    .table-dropdown.is-open > div:first-child {
        height: 32px;
        border: 0 !important;
        box-shadow: none !important;
        background: transparent !important;
        padding: 0 !important;
        font-size: 13px;
        color: inherit !important;
    }

    .table-date-picker {
        font-size: 13px !important;
        line-height: 18px !important;
    }

    .table-dropdown > button span,
    .table-dropdown.is-open > div:first-child span {
        color: inherit !important;
    }

    .table-dropdown.is-open .ant-input {
        background: transparent !important;
        color: inherit !important;
        box-shadow: none !important;
    }

    .table-dropdown svg {
        color: ${colorConstants.iconMuted};
    }

    .cell-edit-fade-in {
        animation: cell-edit-fade-in 160ms ease-out;
    }

    @keyframes cell-edit-fade-in {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }

    .table-cell-input.ant-input-affix-wrapper,
    .table-cell-input.ant-input-affix-wrapper:hover {
        background: transparent !important;
        opacity: 0.55;
        transition: opacity 160ms ease-out, background 160ms ease-out, color 160ms ease-out;
    }

    .table-cell-input.ant-input-affix-wrapper.is-row-selected,
    .table-cell-input.ant-input-affix-wrapper-focused,
    .table-cell-input.ant-input-affix-wrapper-focused:hover {
        background: ${colorConstants.white} !important;
        color: ${colorConstants.sliderLabelText} !important;
        opacity: 1;
    }

    .table-cell-input.ant-input-affix-wrapper .ant-input,
    .table-cell-input.ant-input-affix-wrapper .ant-input-prefix,
    .table-cell-input.ant-input-affix-wrapper .ant-input-suffix {
        background: transparent !important;
        color: inherit !important;
        transition: color 160ms ease-out;
    }

    .table-cell-input.ant-input-affix-wrapper-focused .ant-input,
    .table-cell-input.ant-input-affix-wrapper-focused .ant-input-prefix,
    .table-cell-input.ant-input-affix-wrapper-focused .ant-input-suffix {
        color: ${colorConstants.sliderLabelText} !important;
    }
`;

const StyledFooter = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 12px;
    border-top: 1px solid ${colorConstants.tableFooterBorder};
    font-size: 12px;
    color: ${colorConstants.toolbarLabelGrey};
`;

const StyledAddRowButton = styled(Button)`
    &.ant-btn {
        position: absolute;
        right: 10px;
        bottom: 34px;
        width: 28px;
        height: 28px;
        border-radius: 8px;
        border: 1px solid ${colorConstants.neutralBorder};
        color: ${colorConstants.neutralText};
        background: ${colorConstants.white};
        box-shadow: 0 2px 8px ${colorConstants.blackTransparent08};
    }
`;

const DimensionExpandChevronIcon = ({ isExpanded }: { isExpanded: boolean }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        className={`dimension-expand-chevron${isExpanded ? ' is-expanded' : ''}`}
        aria-hidden="true"
    >
        <path
            d="M4.47271 5.52876C4.21236 5.26841 3.79035 5.26841 3.53 5.52876C3.26965 5.78911 3.26965 6.21112 3.53 6.47147L7.53 10.4715C7.79035 10.7318 8.21236 10.7318 8.47271 10.4715L12.4727 6.47147C12.7331 6.21112 12.7331 5.78911 12.4727 5.52876C12.2124 5.26841 11.7903 5.26841 11.53 5.52876L8.00135 9.0574L4.47271 5.52876Z"
            fill={colorConstants.axisLabelGrey}
        />
    </svg>
);

const AI_HEADER_TOOLTIP_OVERLAY_STYLE = {
    padding: 0,
    background: 'transparent',
    boxShadow: 'none',
    maxWidth: 'none',
};

const wrapWithTooltip = (node: React.ReactNode, showTooltip?: boolean, tooltipText?: string) => {
    if (!showTooltip || !tooltipText) return node;
    return <Tooltip title={tooltipText}>{node}</Tooltip>;
};

const AiColumnHeaderInfoIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <g clipPath="url(#ai-column-header-info-clip)">
            <path
                d="M10.5 6C10.5 3.51472 8.48528 1.5 6 1.5C3.51472 1.5 1.5 3.51472 1.5 6C1.5 8.48528 3.51472 10.5 6 10.5C8.48528 10.5 10.5 8.48528 10.5 6ZM5.5 8V6C5.5 5.72386 5.72386 5.5 6 5.5C6.27614 5.5 6.5 5.72386 6.5 6V8C6.5 8.27614 6.27614 8.5 6 8.5C5.72386 8.5 5.5 8.27614 5.5 8ZM6.00488 3.5C6.28103 3.5 6.50488 3.72386 6.50488 4C6.50488 4.27614 6.28103 4.5 6.00488 4.5H6C5.72386 4.5 5.5 4.27614 5.5 4C5.5 3.72386 5.72386 3.5 6 3.5H6.00488ZM11.5 6C11.5 9.03757 9.03757 11.5 6 11.5C2.96243 11.5 0.5 9.03757 0.5 6C0.5 2.96243 2.96243 0.5 6 0.5C9.03757 0.5 11.5 2.96243 11.5 6Z"
                fill={colorConstants.toolbarLabelGrey}
            />
        </g>
        <defs>
            <clipPath id="ai-column-header-info-clip">
                <rect width="12" height="12" fill={colorConstants.white} />
            </clipPath>
        </defs>
    </svg>
);

const wrapAiColumnHeaderTooltip = (node: React.ReactNode, showTooltip?: boolean) => {
    if (!showTooltip) return node;

    return (
        <Tooltip
            title={<AiColumnHeaderTooltip />}
            placement="right"
            showArrow={false}
            overlayClassName="ai-column-header-tooltip"
            overlayStyle={{ maxWidth: 'none' }}
            overlayInnerStyle={AI_HEADER_TOOLTIP_OVERLAY_STYLE}
            getPopupContainer={() => document.body}
        >
            <span className="inline-flex cursor-pointer" data-testid={PPA_SIM_TABLE_AI_INFO_BTN}>
                {node}
            </span>
        </Tooltip>
    );
};

const collectColumnFilterOptions = (rows: any[], dataIndex: string): string[] => {
    const values = new Set<string>();
    const visit = (row: any) => {
        const value = String(row?.[dataIndex] ?? '').trim();
        if (value) values.add(value);
        if (Array.isArray(row?.children)) {
            row.children.forEach(visit);
        }
    };
    rows.forEach(visit);
    return [...values];
};

const GenericAntdScenarioTable = ({
    tableUiConfig,
    sourceRows,
    isLoading = false,
    tableSort,
    setTableSort,
    columnFilterSortByKey,
    setColumnFilterSortByKey,
}: GenericAntdScenarioTableProps) => {
    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
    const isFlattened = tableUiConfig.behavior.isFlattened;
    const allDimensionValue = tableUiConfig.aiColumn.allDimensionValue;
    const dimensionColumnKeys = getDimensionColumnKeys(tableUiConfig.standardColumns);
    const flattenedHiddenDimensionKeys = getFlattenedHiddenDimensionKeys(tableUiConfig.standardColumns);
    const [expandedRowKeys, setExpandedRowKeys] = useState<Key[]>(() => collectExpandableRowKeys(sourceRows));
    const [editableValueByCell, setEditableValueByCell] = useState<Record<string, string>>({});
    const [hoveredEditableCellId, setHoveredEditableCellId] = useState<string | null>(null);
    const [activeEditableCellId, setActiveEditableCellId] = useState<string | null>(null);
    const activeColumnSortOrder =
        tableSort && !tableSort.sort_by ? toUiSortOrder(tableSort.order) : null;
    const kpiSort =
        tableSort?.sort_by != null
            ? { groupKey: tableSort.id, direction: tableSort.order, optionKey: tableSort.sort_by }
            : null;

    const tableHeight = useRef<number | null>(0);
    tableHeight.current = useFillHeightToFitScreen({
        id: tableUiConfig.ids.container,
        height: tableHeight.current,
        buffer: tableUiConfig.table.heightBuffer,
        triggerDummyRender: true,
    });

    const resolvedTableHeight = useMemo(() => {
        const measuredHeight =
            typeof tableHeight.current === 'number' && Number.isFinite(tableHeight.current)
                ? tableHeight.current
                : 0;
        return Math.max(tableUiConfig.table.minBodyHeight, measuredHeight);
    }, [tableHeight.current, tableUiConfig.table.minBodyHeight]);

    useEffect(() => {
        setExpandedRowKeys(collectExpandableRowKeys(sourceRows));
    }, [sourceRows]);

    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            const tableContainer = document.getElementById(tableUiConfig.ids.container);
            if (tableContainer?.contains(event.target as Node)) return;
            setActiveEditableCellId(null);
        };

        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [tableUiConfig.ids.container]);

    const isRowSelected = (rowKey: Key) => selectedRowKeys.includes(rowKey);
    const visibleEditableCellId = hoveredEditableCellId ?? activeEditableCellId;
    const isCellEditable = (rowKey: Key, cellId: string) =>
        isRowSelected(rowKey) || visibleEditableCellId === cellId;

    const tableData = useMemo(
        () => (isFlattened ? flattenTableRows(sourceRows) : sourceRows),
        [isFlattened, sourceRows],
    );

    const updateCellValue = (cellId: string, value: string) => {
        setEditableValueByCell((previous) => ({
            ...previous,
            [cellId]: value,
        }));
    };

    const updateColumnCheckboxValues = (columnKey: string, rowKeys: Key[], checked: boolean) => {
        const nextValue = checked ? 'true' : 'false';
        setEditableValueByCell((previous) => {
            const nextValues = { ...previous };
            rowKeys.forEach((rowKey) => {
                nextValues[getCellId(rowKey, columnKey)] = nextValue;
            });
            return nextValues;
        });
    };

    const getCellEditableRawValue = (cellId: string, fallbackRawValue: any) =>
        editableValueByCell[cellId] ?? String(fallbackRawValue ?? '');

    const repeatedDimensionCellIds = useMemo(
        () =>
            getRepeatedDimensionCellIds(
                collectVisibleTableRows(tableData, expandedRowKeys, isFlattened),
                tableUiConfig.standardColumns.filter((columnConfig) => columnConfig.isDimension),
                (row, columnKey, dataIndex) =>
                    getCellEditableRawValue(getCellId(row.key, columnKey), row[dataIndex]).trim(),
                allDimensionValue,
            ),
        [allDimensionValue, editableValueByCell, expandedRowKeys, isFlattened, tableData, tableUiConfig.standardColumns],
    );

    const isRepeatedDimensionValue = (record: any, columnKey: string) =>
        repeatedDimensionCellIds.has(getCellId(record.key, columnKey));

    const toggleRowExpanded = (rowKey: Key) => {
        setExpandedRowKeys((previousKeys) => {
            if (previousKeys.includes(rowKey)) {
                return previousKeys.filter((key) => key !== rowKey);
            }
            return [...previousKeys, rowKey];
        });
    };

    const renderExpandCollapseButton = (record: any, variant: 'ai' | 'dimension' = 'ai') => {
        const isExpanded = expandedRowKeys.includes(record.key);

        return (
            <button
                type="button"
                className="ai-expand-btn"
                onClick={(event) => {
                    event.stopPropagation();
                    toggleRowExpanded(record.key);
                }}
                aria-label={
                    isExpanded
                        ? tableUiConfig.aiColumn.expandAriaLabel.expanded
                        : tableUiConfig.aiColumn.expandAriaLabel.collapsed
                }
            >
                {variant === 'dimension' ? (
                    <DimensionExpandChevronIcon isExpanded={isExpanded} />
                ) : (
                    <span
                        className="relative flex size-[12px] shrink-0 items-center justify-center rounded-[4px]"
                        style={{
                            backgroundColor: tableUiConfig.aiColumn.icon.backgroundColor,
                        }}
                    >
                        <span
                            className="absolute h-[1.3px] w-[6.9px] rounded-[1px]"
                            style={{
                                backgroundColor: tableUiConfig.aiColumn.icon.lineColor,
                            }}
                        />
                        {!isExpanded ? (
                            <span
                                className="absolute h-[6.9px] w-[1.3px] rounded-[1px]"
                                style={{
                                    backgroundColor: tableUiConfig.aiColumn.icon.lineColor,
                                }}
                            />
                        ) : null}
                    </span>
                )}
            </button>
        );
    };

    const canExpandFromAllValue = (record: any, value: any, columnKey: string) =>
        !isFlattened &&
        isAllDimensionValue(value, allDimensionValue) &&
        getExpandableDimensionKey(record, dimensionColumnKeys, allDimensionValue) === columnKey;

    const bindEditModeHandlers = (cellId: string) => ({
        onMouseEnter: () => {
            if (!tableUiConfig.behavior.editOnHover) return;
            setHoveredEditableCellId(cellId);
        },
        onMouseLeave: () => {
            if (!tableUiConfig.behavior.editOnHover) return;
            setHoveredEditableCellId((previous) => (previous === cellId ? null : previous));
        },
        onClick: () => {
            if (!tableUiConfig.behavior.editOnClick) return;
            setActiveEditableCellId(cellId);
        },
    });

    const renderHeaderTitle = (columnConfig: any) => {
        if (columnConfig.filterSortable) {
            const applied = columnFilterSortByKey[columnConfig.key];
            return (
                <ColumnFilterSortHeader
                    columnKey={columnConfig.key}
                    title={columnConfig.title}
                    options={collectColumnFilterOptions(sourceRows, columnConfig.dataIndex)}
                    labels={tableUiConfig.columnFilterSort}
                    value={{
                        sortOrder: applied?.sortOrder ?? null,
                        selectedValues: applied?.selectedValues ?? [],
                    }}
                    onApply={(nextValue) => {
                        setColumnFilterSortByKey((previous) => {
                            const nextEntries = Object.entries(previous).map(([key, value]) => [
                                key,
                                { ...value, sortOrder: key === columnConfig.key ? nextValue.sortOrder : null },
                            ]);
                            return {
                                ...Object.fromEntries(nextEntries),
                                [columnConfig.key]: nextValue,
                            };
                        });
                        setTableSort(
                            nextValue.sortOrder
                                ? { id: columnConfig.key, order: toApiSortOrder(nextValue.sortOrder) }
                                : tableSort?.id === columnConfig.key
                                  ? null
                                  : tableSort,
                        );
                    }}
                />
            );
        }

        if (columnConfig.sortable) {
            return (
                <DimensionColumnHeader
                    columnKey={columnConfig.key}
                    title={columnConfig.title}
                    sortOrder={tableSort?.id === columnConfig.key && !tableSort?.sort_by ? activeColumnSortOrder : null}
                    onSortChange={(order) => {
                        setColumnFilterSortByKey((previous) =>
                            Object.fromEntries(
                                Object.entries(previous).map(([key, value]) => [key, { ...value, sortOrder: null }]),
                            ),
                        );
                        setTableSort(order ? { id: columnConfig.key, order: toApiSortOrder(order) } : null);
                    }}
                />
            );
        }

        const baseTitle = (
            <span className="leading-[16px]" data-testid={ppaSimTableColumnHeaderTestId(columnConfig.key)}>
                {columnConfig.title}
            </span>
        );

        const titleWithLock = columnConfig.showLockIconInTitle ? (
            <div className="flex items-center gap-1">
                <LockOutlined className="!text-[11px]" style={{ color: colorConstants.toolbarLabelGrey }} />
                {baseTitle}
            </div>
        ) : (
            baseTitle
        );

        return wrapWithTooltip(titleWithLock, columnConfig.showTitleTooltip, columnConfig.titleTooltipText);
    };

    const renderEditableDropdownCell = (columnConfig: any, value: any, record: any) => {
        const cellId = getCellId(record.key, columnConfig.key);
        const fallbackValue = String(value ?? columnConfig.emptyDisplay ?? '');
        const resolvedValue = getCellEditableRawValue(cellId, fallbackValue);
        const isCellInEditMode = isCellEditable(record.key, cellId);

        if (isCellInEditMode) {
            const sharedProps = {
                className: `table-dropdown${isRowSelected(record.key) ? ' is-row-selected' : ''}`,
                options: columnConfig.editor.options,
                value: resolvedValue,
                onChange: (nextValue: any) => updateCellValue(cellId, String(nextValue)),
                placeholder: columnConfig.editor.placeholder,
                style: { color: columnConfig.displayColor },
            };

            return (
                <div
                    key={`${cellId}-edit`}
                    className="cell-edit-fade-in flex min-h-[32px] items-center"
                    {...bindEditModeHandlers(cellId)}
                >
                    {columnConfig.editor.kind === 'searchableDropdown' ? (
                        <GenericSearchableDropdownControl {...sharedProps} />
                    ) : (
                        <GenericDropdownControl {...sharedProps} />
                    )}
                </div>
            );
        }

        const displayNode = (
            <TwoLineCellText
                text={resolvedValue || columnConfig.emptyDisplay}
                className={columnConfig.displayClassName}
                style={{ color: columnConfig.displayColor }}
            />
        );

        return (
            <div
                key={`${cellId}-display`}
                className="cell-edit-fade-in flex min-h-[32px] items-center"
                style={{
                    opacity:
                        !isFlattened &&
                        isRepeatedDimensionValue(record, columnConfig.key) &&
                        !isAllDimensionValue(resolvedValue || columnConfig.emptyDisplay, allDimensionValue)
                            ? tableUiConfig.table.repeatedValueOpacity
                            : 1,
                }}
                {...bindEditModeHandlers(cellId)}
            >
                {displayNode}
            </div>
        );
    };

    const renderDatePickerCell = (columnConfig: any, value: any, record: any) => {
        const cellId = getCellId(record.key, columnConfig.key);
        const resolvedValue = getCellEditableRawValue(cellId, value);

        return (
            <div className="flex min-h-[32px] w-full items-center">
                <GenericDatePickerControl
                    value={resolvedValue}
                    onChange={(nextValue) => updateCellValue(cellId, nextValue)}
                    placeholder={columnConfig.editor?.placeholder ?? columnConfig.emptyDisplay}
                    startWeekday={columnConfig.editor?.startWeekday}
                    showTooltip={columnConfig.editor?.showTooltip}
                    tooltipText={columnConfig.editor?.tooltipText}
                    className="table-date-picker"
                    style={{ fontSize: 13, lineHeight: '18px' }}
                />
            </div>
        );
    };

    const renderCheckboxCell = (columnConfig: any, value: any, record: any) => {
        const cellId = getCellId(record.key, columnConfig.key);
        const isChecked = isCheckboxChecked(getCellEditableRawValue(cellId, value));
        const tooltipItems = columnConfig.editor?.checkedTooltipItems ?? [];

        return (
            <div className="flex min-h-[32px] w-full items-center justify-center">
                <GenericCheckboxControl
                    checked={isChecked}
                    onChange={(nextChecked) => updateCellValue(cellId, nextChecked ? 'true' : 'false')}
                    showTooltip={Boolean(columnConfig.editor?.showCheckedTooltip && tooltipItems.length)}
                    staticTooltipText={<GenericCheckboxTooltipList items={tooltipItems} />}
                />
            </div>
        );
    };

    const renderCheckboxColumnHeader = (columnConfig: any) => {
        const allRows = collectAllRows(tableData);
        const checkedCount = allRows.filter((row) =>
            isCheckboxChecked(
                getCellEditableRawValue(getCellId(row.key, columnConfig.key), row[columnConfig.dataIndex]),
            ),
        ).length;
        const isAllChecked = allRows.length > 0 && checkedCount === allRows.length;
        const isIndeterminate = checkedCount > 0 && !isAllChecked;

        return (
            <div
                className="flex w-full items-center gap-2"
                data-testid={ppaSimTableColumnHeaderTestId(columnConfig.key)}
            >
                <span data-testid={ppaSimTableColumnSelectAllTestId(columnConfig.key)}>
                    <GenericCheckboxControl
                        checked={isAllChecked}
                        indeterminate={isIndeterminate}
                        onChange={(nextChecked) =>
                            updateColumnCheckboxValues(
                                columnConfig.key,
                                allRows.map((row) => row.key),
                                nextChecked,
                            )
                        }
                    />
                </span>
                <span className="whitespace-nowrap text-[12px] font-semibold leading-[18px]">{columnConfig.title}</span>
            </div>
        );
    };

    const renderEditableInputCell = (metricConfig: any, groupConfig: any, value: any, record: any) => {
        const metricColumnKey = `${groupConfig.key}:${metricConfig.key}`;
        const cellId = getCellId(record.key, metricColumnKey);
        const rawValue = getCellEditableRawValue(cellId, value);
        const isCellInEditMode = isCellEditable(record.key, cellId);
        const displayText = resolveDisplayText(
            rawValue,
            metricConfig.displayFormat,
            metricConfig.displayFormat?.emptyDisplay ?? '',
        );

        return (
            <div
                key={`${cellId}-${isCellInEditMode ? 'edit' : 'display'}`}
                className="cell-edit-fade-in flex min-h-[32px] items-center justify-center"
                {...bindEditModeHandlers(cellId)}
            >
                {isCellInEditMode ? (
                    <GenericPrefixedInputBoxControl
                        showSave={tableUiConfig.behavior.showSaveActions}
                        prefixText={metricConfig.editor?.prefixText}
                        suffix={
                            <span className="inline-flex items-center gap-[2px]">
                                {metricConfig.editor?.suffixText ? (
                                    <span>{metricConfig.editor.suffixText}</span>
                                ) : null}
                                <DownSvg dropDownStroke={colorConstants.iconMuted} />
                            </span>
                        }
                        value={rawValue}
                        onChange={(nextValue) => updateCellValue(cellId, nextValue)}
                        allowedValueType={metricConfig.editor?.allowedValueType}
                        allowDecimal={metricConfig.editor?.allowDecimal}
                        decimalPlaces={metricConfig.editor?.decimalPlaces}
                        className={`table-cell-input${isRowSelected(record.key) ? ' is-row-selected' : ''} !h-full !rounded-[4px] !border-[0.5px] !px-[6px] [&_.ant-input-prefix]:!me-[2px] [&_.ant-input-prefix]:!text-[13px] [&_.ant-input]:!text-[13px] [&_.ant-input]:!leading-[18px] [&_.ant-input]:!text-right`}
                        style={{
                            borderColor: colorConstants.tableCellBorder,
                            color: metricConfig.displayColor,
                            background: 'transparent',
                        }}
                    />
                ) : (
                    <span className={metricConfig.displayClassName} style={{ color: metricConfig.displayColor }}>
                        {displayText}
                    </span>
                )}
            </div>
        );
    };

    const kpiGroupColumns = useMemo(
        () =>
            tableUiConfig.kpiGroups.map((groupConfig) => ({
                title: (
                    <KpiColumnGroupHeader
                        groupKey={groupConfig.key}
                        title={groupConfig.title}
                        options={groupConfig.sortType === 'kpi' ? DEFAULT_KPI_SORT_OPTIONS : []}
                        isApplied={kpiSort?.groupKey === groupConfig.key}
                        value={
                            kpiSort?.groupKey === groupConfig.key
                                ? { direction: kpiSort.direction, optionKey: kpiSort.optionKey }
                                : DEFAULT_KPI_SORT_VALUE
                        }
                        onApply={(nextValue) => {
                            setColumnFilterSortByKey((previous) =>
                                Object.fromEntries(
                                    Object.entries(previous).map(([key, value]) => [key, { ...value, sortOrder: null }]),
                                ),
                            );
                            setTableSort({
                                id: groupConfig.key,
                                order: nextValue.direction,
                                sort_by: nextValue.optionKey,
                            });
                        }}
                    />
                ),
                key: groupConfig.key,
                children: groupConfig.metrics.map((metricConfig: any) => {
                    const dataIndex = getKpiDataIndex(groupConfig.key, metricConfig.key);
                    return {
                        title: metricConfig.title,
                        dataIndex,
                        key: dataIndex,
                        width: 120,
                        render: (value: any, record: any) => {
                            if (metricConfig.cellType === 'editableInput') {
                                return renderEditableInputCell(metricConfig, groupConfig, value, record);
                            }

                            return (
                                <span
                                    className={metricConfig.displayClassName}
                                    style={{ color: metricConfig.displayColor }}
                                >
                                    {resolveDisplayText(
                                        value,
                                        metricConfig.displayFormat,
                                        metricConfig.displayFormat?.emptyDisplay ?? '',
                                    )}
                                </span>
                            );
                        },
                    };
                }),
            })),
        [editableValueByCell, selectedRowKeys, hoveredEditableCellId, activeEditableCellId, kpiSort, tableUiConfig.kpiGroups],
    );

    const defaultKpiColumns = useMemo(
        () =>
            tableUiConfig.defaultKpiColumns.map((columnConfig) => ({
                title:
                    columnConfig.cellType === 'checkbox'
                        ? renderCheckboxColumnHeader(columnConfig)
                        : renderHeaderTitle(columnConfig),
                dataIndex: columnConfig.dataIndex,
                key: columnConfig.key,
                width: columnConfig.width,
                onHeaderCell: () =>
                    columnConfig.cellType === 'checkbox' ? { className: 'table-checkbox-header' } : {},
                onCell: () => (columnConfig.cellType === 'checkbox' ? { className: 'table-checkbox-cell' } : {}),
                render: (value: any, record: any) => {
                    if (columnConfig.cellType === 'datePicker') {
                        return renderDatePickerCell(columnConfig, value, record);
                    }

                    if (columnConfig.cellType === 'checkbox') {
                        return renderCheckboxCell(columnConfig, value, record);
                    }

                    return String(value ?? columnConfig.emptyDisplay ?? '');
                },
            })),
        [editableValueByCell, tableData, tableUiConfig.defaultKpiColumns],
    );

    const standardColumns = useMemo(
        () =>
            tableUiConfig.standardColumns
                .filter((columnConfig) => !isFlattened || !flattenedHiddenDimensionKeys.has(columnConfig.key))
                .map((columnConfig) => ({
                    title: renderHeaderTitle(columnConfig),
                    dataIndex: columnConfig.dataIndex,
                    key: columnConfig.key,
                    width: columnConfig.width,
                    fixed: columnConfig.fixed,
                    render: (value: any, record: any) => {
                        if (columnConfig.cellType === 'editableDropdown') {
                            return renderEditableDropdownCell(columnConfig, value, record);
                        }

                        if (columnConfig.cellType === 'datePicker') {
                            return renderDatePickerCell(columnConfig, value, record);
                        }

                        if (columnConfig.cellType === 'lockedMetric') {
                            return (
                                <span>
                                    {resolveDisplayText(value, columnConfig.displayFormat, columnConfig.emptyDisplay ?? '')}
                                </span>
                            );
                        }

                        const displayText = String(value ?? columnConfig.emptyDisplay ?? '');
                        const isRepeatedValue =
                            !isFlattened &&
                            isRepeatedDimensionValue(record, columnConfig.key) &&
                            !isAllDimensionValue(displayText, allDimensionValue);
                        const showExpandCollapseIcon = canExpandFromAllValue(record, displayText, columnConfig.key);

                        return (
                            <span className="flex min-w-0 items-center justify-between gap-1">
                                <TwoLineCellText
                                    text={displayText}
                                    className="min-w-0 flex-1"
                                    style={{
                                        opacity: isRepeatedValue ? tableUiConfig.table.repeatedValueOpacity : 1,
                                    }}
                                />
                                {showExpandCollapseIcon ? renderExpandCollapseButton(record, 'dimension') : null}
                            </span>
                        );
                    },
                })),
        [
            editableValueByCell,
            selectedRowKeys,
            hoveredEditableCellId,
            activeEditableCellId,
            expandedRowKeys,
            isFlattened,
            repeatedDimensionCellIds,
            tableUiConfig.standardColumns,
            tableUiConfig.table.repeatedValueOpacity,
            tableSort,
            columnFilterSortByKey,
        ],
    );

    const columns = useMemo<any[]>(() => {
        const aiColumn = tableUiConfig.behavior.showAiColumn
            ? [
                  {
                      title: (
                          <span
                              className="inline-flex items-center gap-1 leading-[16px]"
                              data-testid={PPA_SIM_TABLE_AI_HEADER}
                          >
                              {tableUiConfig.aiColumn.title}
                              {wrapAiColumnHeaderTooltip(
                                  <AiColumnHeaderInfoIcon />,
                                  tableUiConfig.aiColumn.showTitleTooltip,
                              )}
                          </span>
                      ),
                      dataIndex: tableUiConfig.aiColumn.dataIndex,
                      key: tableUiConfig.aiColumn.key,
                      width: tableUiConfig.aiColumn.width,
                      fixed: tableUiConfig.aiColumn.fixed,
                      render: (value: any, record: any) => {
                          const hasAiLabel = Boolean(String(value ?? '').trim());
                          const showAiExpand =
                              !isFlattened &&
                              isAiExpandableLabel(value, tableUiConfig.aiColumn.expandableLabels) &&
                              rowHasExpandableChildren(record);

                          if (!hasAiLabel && !record.parentHint) return null;

                          return (
                              <div className="flex flex-col leading-[1.1]">
                                  <div className="flex items-center gap-1">
                                      {showAiExpand ? renderExpandCollapseButton(record) : null}
                                      {hasAiLabel ? (
                                          <span
                                              className="whitespace-nowrap text-[12px] font-medium leading-[22px]"
                                              style={{ color: colorConstants.sliderTooltipBg }}
                                          >
                                              {value}
                                          </span>
                                      ) : null}
                                  </div>
                                  {record.parentHint ? (
                                      <span
                                          className={tableUiConfig.aiColumn.parentHintClassName}
                                          style={{ color: tableUiConfig.aiColumn.parentHintColor }}
                                      >
                                          {record.parentHint}
                                      </span>
                                  ) : null}
                              </div>
                          );
                      },
                  },
              ]
            : [];

        return [...aiColumn, ...standardColumns, ...kpiGroupColumns, ...defaultKpiColumns];
    }, [
        defaultKpiColumns,
        expandedRowKeys,
        isFlattened,
        kpiGroupColumns,
        standardColumns,
        tableSort,
        tableUiConfig.aiColumn,
        tableUiConfig.behavior.showAiColumn,
    ]);

    return (
        <StyledTableShell id={tableUiConfig.ids.container} data-testid={PPA_SIM_TABLE}>
            {isLoading ? <div data-testid={PPA_SIM_TABLE_LOADING} hidden /> : null}
            {!isLoading && tableData.length === 0 ? <div data-testid={PPA_SIM_TABLE_EMPTY} hidden /> : null}
            <Spin spinning={isLoading}>
                <Table
                    columns={columns}
                    dataSource={tableData}
                    rowKey="key"
                    pagination={false}
                    loading={isLoading}
                    scroll={{ x: tableUiConfig.table.scrollX, y: resolvedTableHeight }}
                    size={tableUiConfig.table.size}
                    rowSelection={
                        tableUiConfig.behavior.showRowSelection
                            ? {
                                  selectedRowKeys,
                                  onChange: setSelectedRowKeys,
                                  columnWidth: tableUiConfig.table.rowSelectionWidth,
                                  renderCell: (_checked, record, _index, originNode) => (
                                      <span data-testid={ppaSimTableRowSelectTestId(record?.key ?? '')}>
                                          {originNode}
                                      </span>
                                  ),
                              }
                            : undefined
                    }
                    components={{
                        header: {
                            cell: (props: ThHTMLAttributes<HTMLTableCellElement>) => {
                                const isSelectionColumn = String(props.className ?? '').includes(
                                    'ant-table-selection-column',
                                );
                                return (
                                    <th
                                        {...props}
                                        {...(isSelectionColumn ? { 'data-testid': PPA_SIM_TABLE_SELECT_ALL } : {})}
                                    />
                                );
                            },
                        },
                    }}
                    expandable={
                        isFlattened
                            ? undefined
                            : {
                                  expandedRowKeys,
                                  showExpandColumn: false,
                              }
                    }
                    showSorterTooltip={false}
                />
            </Spin>
            {tableUiConfig.behavior.showAddRow ? (
                <StyledAddRowButton
                    size="small"
                    icon={<PlusOutlined />}
                    aria-label={tableUiConfig.actionButton.ariaLabel}
                    data-testid={PPA_SIM_TABLE_ADD_ROW_BTN}
                />
            ) : null}
            {tableUiConfig.footer.recordLabel || tableUiConfig.footer.statusLabel || tableUiConfig.footer.refreshLabel ? (
                <StyledFooter data-testid={PPA_SIM_TABLE_FOOTER}>
                    <span>{tableUiConfig.footer.recordLabel}</span>
                    <span>
                        {tableUiConfig.footer.statusLabel}
                        {tableUiConfig.footer.statusLabel && tableUiConfig.footer.refreshLabel ? '\u00a0\u00a0\u00a0\u00a0' : ''}
                        {tableUiConfig.footer.refreshLabel}
                    </span>
                </StyledFooter>
            ) : null}
        </StyledTableShell>
    );
};

type GenericAntdScenarioTableContainerProps = {
    projectId?: number | string | null;
    scenarioId?: number | string | null;
    resolvedFilters?: Record<string, number[]> | null;
    appliedFilters?: SimulatorAppliedFilters;
    searchInput?: SimulatorTableSearchInput;
    nestDimensions?: boolean;
    columnVisibility?: PPAScenarioColumnVisibilityInput[];
    groupBy?: SimulationTableGroupByItem[];
    activeLensId?: number | null;
};

const GenericAntdScenarioTableContainer = (props: GenericAntdScenarioTableContainerProps) => {
    const {
        tableUiConfig,
        sourceRows,
        isLoading,
        tableSort,
        setTableSort,
        columnFilterSortByKey,
        setColumnFilterSortByKey,
    } = useGenericAntdScenarioTable(props);

    return (
        <GenericAntdScenarioTable
            tableUiConfig={tableUiConfig}
            sourceRows={sourceRows}
            isLoading={isLoading}
            tableSort={tableSort}
            setTableSort={setTableSort}
            columnFilterSortByKey={columnFilterSortByKey}
            setColumnFilterSortByKey={setColumnFilterSortByKey}
        />
    );
};

export default GenericAntdScenarioTableContainer;
